import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Leaf, Users, HeartHandshake, Sprout,
  MapPin, Calendar, GraduationCap,
} from "lucide-react";
import { useLang } from "../LanguageContext";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_ecole-pleine-nature/artifacts/he4qpx18_chem.jpg";

const PILLAR_ICONS = [Sprout, Users, HeartHandshake, Leaf];
const FACT_ICONS = [GraduationCap, MapPin, Calendar, Users];

export default function Home() {
  const { t } = useLang();
  const pillars = t("home.pillars") || [];
  const facts = t("home.facts") || [];

  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-page pt-16 sm:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 animate-fade-up">
              <p className="eyebrow mb-5" data-testid="hero-eyebrow">{t("hero.eyebrow")}</p>
              <h1 className="text-5xl sm:text-6xl lg:text-[68px] leading-[1.05] font-heading font-medium text-ink mb-6">
                {t("hero.title")}
              </h1>
              <p className="text-lg sm:text-xl text-ink-muted leading-relaxed max-w-xl mb-4">
                {t("hero.subtitle")}
              </p>
              <p className="text-base text-ink-muted/90 leading-relaxed max-w-xl mb-10">
                {t("hero.intro")}
              </p>

              <div className="flex flex-col sm:flex-row gap-3" data-testid="hero-cta-group">
                <Link to="/comprendre" className="btn-primary" data-testid="cta-informer">
                  {t("hero.cta_informer")} <ArrowRight size={18} />
                </Link>
                <Link to="/s-engager" className="btn-secondary" data-testid="cta-engager">
                  {t("hero.cta_engager")}
                </Link>
                <Link to="/inscrire" className="btn-outline" data-testid="cta-inscrire">
                  {t("hero.cta_inscrire")}
                </Link>
              </div>

              <div className="mt-12 flex items-center gap-5 text-sm text-ink-muted">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-cream-50 bg-forest-light grid place-items-center text-forest text-xs font-semibold"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <span className="leading-tight">
                  <strong className="text-ink">{t("hero.members_count")}</strong> {t("hero.members_caption")}
                </span>
              </div>
            </div>

            <div className="lg:col-span-6 relative animate-fade-in">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-lift bg-cream-100">
                <img
                  src={HERO_IMG}
                  alt="École Pleine Nature"
                  className="w-full h-auto block"
                />
                <div className="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:left-7 sm:right-auto bg-cream-50/95 backdrop-blur rounded-2xl px-5 py-4 shadow-soft max-w-sm">
                  <p className="font-accent text-[22px] leading-tight text-forest">
                    {t("hero.quote_text")}
                  </p>
                  <p className="text-xs text-ink-muted mt-1">{t("hero.quote_caption")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-cream-100/60 py-20 sm:py-28" data-testid="section-pillars">
        <div className="container-page">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-3">{t("home.pillars_eyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink">
              {t("home.pillars_title")}
            </h2>
            <p className="mt-4 text-ink-muted leading-relaxed">{t("home.pillars_intro")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((c, i) => {
              const Icon = PILLAR_ICONS[i] || Leaf;
              return (
                <div
                  key={i}
                  className="card-soft p-7 hover:shadow-lift hover:-translate-y-1 transition-all duration-300"
                  data-testid={`pillar-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-forest-light grid place-items-center mb-5 text-forest">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-ink mb-2">{c.title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* En pratique strip */}
      <section className="py-20 sm:py-28" data-testid="section-pratique">
        <div className="container-page">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-3">{t("home.practical_eyebrow")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink mb-5">
                {t("home.practical_title")}
              </h2>
              <p className="text-ink-muted leading-loose mb-10">{t("home.practical_intro")}</p>
              <Link to="/pratique" className="btn-outline" data-testid="cta-pratique">
                {t("home.practical_cta")} <ArrowRight size={18} />
              </Link>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {facts.map((f, i) => {
                const Icon = FACT_ICONS[i] || MapPin;
                return (
                  <div key={i} className="card-soft p-6 flex items-start gap-4" data-testid={`fact-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-sage-light grid place-items-center text-forest-dark shrink-0">
                      <Icon size={20} strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-heading text-lg text-ink font-semibold leading-tight">{f.label}</div>
                      <div className="text-sm text-ink-muted mt-1">{f.sub}</div>
                    </div>
                  </div>
                );
              })}
              <div className="sm:col-span-2 rounded-3xl bg-forest text-cream-50 p-7 flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-cream-100/70">
                    {t("home.status_label")}
                  </div>
                  <div className="font-heading text-xl mt-1">{t("home.status_value")}</div>
                </div>
                <span className="text-xs px-3 py-1.5 rounded-full bg-cream-50/15 border border-cream-50/20">
                  {t("home.status_badge")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-cream-100/60 py-20 sm:py-28" data-testid="section-testimonials">
        <div className="container-page">
          <div className="max-w-2xl">
            <p className="eyebrow mb-3">{t("home.testimonials_eyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink">
              {t("home.testimonials_title")}
            </h2>
            <p className="mt-4 text-ink-muted leading-relaxed">{t("home.testimonials_intro")}</p>
            <Link to="/temoignages" className="btn-outline mt-8" data-testid="cta-temoignages">
              {t("home.testimonials_cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28" data-testid="section-final-cta">
        <div className="container-page">
          <div className="rounded-[2.5rem] bg-forest-deep text-cream-50 px-8 sm:px-14 py-16 sm:py-20 relative overflow-hidden">
            <div className="relative max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-4">
                {t("home.final_eyebrow")}
              </p>
              <h2 className="font-heading text-3xl sm:text-5xl font-medium leading-tight text-cream-50">
                {t("home.final_title")}
              </h2>
              <p className="mt-5 text-cream-100/80 leading-relaxed">{t("home.final_intro")}</p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/comprendre"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 bg-cream-50 text-forest-deep font-medium hover:bg-cream-100 transition-colors"
                  data-testid="cta-final-decouvrir"
                >
                  {t("home.final_cta_discover")} <ArrowRight size={18} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 border border-cream-50/40 text-cream-50 hover:bg-cream-50/10 transition-colors"
                  data-testid="cta-final-contact"
                >
                  {t("home.final_cta_contact")}
                </Link>
              </div>
            </div>
            <div className="hidden md:block absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-forest opacity-50 blur-3xl" />
            <div className="hidden md:block absolute right-10 top-10 w-40 h-40 rounded-full bg-earth/40 blur-2xl" />
          </div>
        </div>
      </section>
    </div>
  );
}
